import React, { useMemo, useState } from 'react';
import { CogIcon, ViewBoardsIcon, ViewListIcon } from '@heroicons/react/outline';
import ToDoBoardView from './ToDoBoardView';
import ToDoSettings from './ToDoSettings';
import ToDoTableView from './ToDoTableView';
import useCompanyUsers from './useCompanyUsers';
import useTodos from './useTodos';
import useToDoConfig from './useToDoConfig';

function ToDo({ selectedCompany }) {
  const companyId = selectedCompany?.id;
  const [view, setView] = useState('table'); // 'table' | 'board'
  const [settingsVisible, setSettingsVisible] = useState(false);

  const {
    todos,
    loading: todosLoading,
    error: todosError,
    createTodo,
    updateTodo,
    deleteTodo,
    moveTodo,
  } = useTodos(companyId);

  const { statuses, categories, loading: configLoading, error: configError, upsertStatus, deleteStatus, upsertCategory, deleteCategory } =
    useToDoConfig(companyId);

  const { users, loading: usersLoading, error: usersError } = useCompanyUsers(companyId);

  const orderedStatuses = useMemo(() => {
    return [...statuses].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }, [statuses]);

  if (!companyId) {
    return (
      <div className="font-body px-4 pt-28 pb-10 w-[90%] md:w-[80%] max-w-[78rem] mx-auto">
        <div className="rounded-lg bg-white shadow-basic p-4">
          <p className="text-lg font-semibold">ToDo</p>
          <p className="text-sm text-neutral-600 mt-1">Please select a client/company first.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="font-body px-4 pt-20 pb-10 w-[95%] max-w-[92rem] mx-auto">
      <div className="flex items-start gap-3 flex-col md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">ToDo</h1>
          <p className="text-sm text-neutral-600 mt-0.5">Company: {companyId}</p>
        </div>

        <div className="flex items-center gap-2">
          <div className="bg-white shadow-basic rounded-lg p-1 flex items-center">
            <button
              className={
                'px-3 py-2 rounded-md text-sm flex items-center gap-2 transition ' +
                (view === 'table' ? 'bg-slate-900 text-white' : 'hover:bg-slate-100')
              }
              onClick={() => setView('table')}
            >
              <ViewListIcon className="w-5 h-5" />
              Table
            </button>
            <button
              className={
                'px-3 py-2 rounded-md text-sm flex items-center gap-2 transition ' +
                (view === 'board' ? 'bg-slate-900 text-white' : 'hover:bg-slate-100')
              }
              onClick={() => setView('board')}
            >
              <ViewBoardsIcon className="w-5 h-5" />
              Board
            </button>
          </div>

          <button
            className="bg-white shadow-basic rounded-lg p-2 hover:bg-slate-50 transition"
            title="Settings"
            onClick={() => setSettingsVisible(true)}
          >
            <CogIcon className="w-6 h-6" />
          </button>
        </div>
      </div>

      {(todosError || configError) && (
        <div className="mt-4 rounded-lg bg-red-50 border border-red-100 p-3 text-sm text-red-700">
          {todosError ? <div>Todos error: {String(todosError)}</div> : null}
          {configError ? <div>Config error: {String(configError)}</div> : null}
        </div>
      )}

      {usersError && (
        <div className="mt-4 rounded-lg bg-amber-50 border border-amber-100 p-3 text-sm text-amber-800">
          Assignee list error: {String(usersError)}
        </div>
      )}

      {view === 'table' ? (
        <ToDoTableView
          todos={todos}
          todosLoading={todosLoading}
          statuses={orderedStatuses}
          categories={categories}
          users={users}
          usersLoading={usersLoading}
          updateTodo={updateTodo}
          createTodo={createTodo}
          deleteTodo={deleteTodo}
        />
      ) : (
        <ToDoBoardView
          todos={todos}
          todosLoading={todosLoading}
          statuses={orderedStatuses}
          categories={categories}
          users={users}
          updateTodo={updateTodo}
          createTodo={createTodo}
          deleteTodo={deleteTodo}
          moveTodo={moveTodo}
        />
      )}

      <ToDoSettings
        open={settingsVisible}
        onClose={() => setSettingsVisible(false)}
        statuses={orderedStatuses}
        categories={categories}
        upsertStatus={upsertStatus}
        deleteStatus={deleteStatus}
        upsertCategory={upsertCategory}
        deleteCategory={deleteCategory}
        loading={configLoading}
      />
    </div>
  );
}

export default ToDo;
